import { useState, useRef, useCallback, useEffect } from "react";
import { Upload, Loader2, CheckCircle2, AlertCircle, X, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { extractSyncBolS3Error, formatSyncBolS3Error } from "@/lib/sync-bol-s3-error";

interface DocumentUploadProps {
  onComplete: () => void;
  compact?: boolean;
  orderId?: string;
  /** Human-readable order identifier (e.g. AOSC-DETM-00001). Required to
   *  trigger the S3 sync for BoL uploads attached to an order. */
  orderUniqueId?: string;
  /** When set together with orderId, also inserts a row into the matching order_*_images table. */
  documentKind?: "bol" | "tender";
}

type DocState = "uploading" | "done" | "error";

interface DocItem {
  id: string;
  file: File;
  preview: string;
  isPdf: boolean;
  signedUrl: string | null;
  state: DocState;
  extractionId: string | null;
  errorMsg: string;
}

const ACCEPTED_TYPES = "image/jpeg,image/png,image/webp,application/pdf";

function isValidFile(file: File): string | null {
  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) return `${file.name} exceeds 10 MB limit`;
  const validTypes = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
  if (!validTypes.includes(file.type)) return `${file.name} is not a supported file type`;
  return null;
}

export function DocumentUpload({ onComplete, compact = false, orderId, orderUniqueId, documentKind }: DocumentUploadProps) {
  const { user } = useAuth();
  const { tenantId, activeTenant } = useTenant();
  const [docs, setDocs] = useState<DocItem[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const updateDoc = useCallback((id: string, updates: Partial<DocItem>) => {
    setDocs((prev) => prev.map((d) => (d.id === id ? { ...d, ...updates } : d)));
  }, []);

  const processFile = useCallback(async (file: File, itemId: string) => {
    let s3KeyForRollback: string | null = null;
    try {
      const fileExt = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const filePath = `${user?.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      // Sync-then-Persist: if this is a BoL being attached to a specific order,
      // the NetSuite-facing S3 sync MUST succeed before any local persistence.
      // Failure aborts the chain — nothing is uploaded to storage and no DB
      // rows are inserted. Bare /documents uploads (no orderId) and tender
      // uploads skip this step entirely.
      if (orderId && orderUniqueId && (documentKind === "bol" || documentKind === "tender")) {
        const reader = new FileReader();
        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(",")[1] || result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const { data: syncResult, error: syncError } = await supabase.functions.invoke("sync-bol-s3", {
          body: {
            file_data: base64,
            file_name: file.name,
            tenant_slug: activeTenant,
            order_id: orderUniqueId,
            doc_kind: documentKind,
          },
        });

        if (syncError || !syncResult?.success) {
          const detail = await extractSyncBolS3Error(syncError, syncResult);
          throw new Error(`S3 upload failed: ${formatSyncBolS3Error(detail)}`);
        }
        s3KeyForRollback = syncResult?.s3_key ?? null;
      }

      const { error: uploadErr } = await supabase.storage
        .from("idp-uploads")
        .upload(filePath, file, {
          contentType: file.type || undefined,
          upsert: false,
        });
      if (uploadErr) throw new Error(uploadErr.message);

      // Get signed URL for PDF preview
      if (file.type === "application/pdf") {
        const { data: signedData } = await supabase.storage
          .from("idp-uploads")
          .createSignedUrl(filePath, 3600);
        if (signedData?.signedUrl) {
          updateDoc(itemId, { signedUrl: signedData.signedUrl });
        }
      }

      const extractionPayload: any = {
        file_name: file.name,
        file_path: filePath,
        document_type: "pending",
        status: "pending" as any,
        uploaded_by: user?.id,
        uploaded_by_email: user?.email,
      };
      if (tenantId) extractionPayload.tenant_id = tenantId;

      const { data: extraction, error: insertErr } = await supabase
        .from("document_extractions")
        .insert(extractionPayload)
        .select("id")
        .single();
      if (insertErr) throw new Error(insertErr.message);

      // Attach to the order if we're inside an order-scoped dialog.
      if (orderId && documentKind) {
        const table =
          documentKind === "tender" ? "order_tender_images" : "order_bol_images";
        const attachPayload: any = {
          order_id: orderId,
          image_path: filePath,
          image_url: filePath,
          s3_key: s3KeyForRollback,
          file_name: file.name,
        };
        if (tenantId) attachPayload.tenant_id = tenantId;

        const { error: attachErr } = await supabase.from(table).insert(attachPayload);
        if (attachErr) throw new Error(`Attach to order failed: ${attachErr.message}`);
      }

      updateDoc(itemId, { extractionId: extraction.id, state: "done" });
      toast.success(
        orderId && documentKind ? `${file.name}: Attached to order` : `${file.name}: Uploaded`
      );
    } catch (err: any) {
      // Rollback: if we synced to S3 but local persistence failed, delete the
      // S3 object to avoid duplicates on retry.
      if (s3KeyForRollback && orderUniqueId) {
        try {
          await supabase.functions.invoke("sync-bol-s3", {
            body: {
              action: "delete",
              tenant_slug: activeTenant,
              order_id: orderUniqueId,
              s3_key: s3KeyForRollback,
            },
          });
        } catch (cleanupErr) {
          console.error("S3 rollback failed for key", s3KeyForRollback, cleanupErr);
          toast.error(
            `S3 cleanup failed — manual review needed for s3_key=${s3KeyForRollback}`
          );
        }
      }
      updateDoc(itemId, { state: "error", errorMsg: err.message || "An error occurred" });
      toast.error(`${file.name}: Upload failed`);
    }
  }, [user, tenantId, activeTenant, updateDoc, orderId, orderUniqueId, documentKind]);

  const addFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const newItems: DocItem[] = [];

    for (const file of fileArray) {
      const validationError = isValidFile(file);
      if (validationError) {
        toast.error(validationError);
        continue;
      }
      const id = `${Date.now()}-${Math.random().toString(36).substring(7)}`;
      newItems.push({
        id,
        file,
        preview: URL.createObjectURL(file),
        isPdf: file.type === "application/pdf",
        signedUrl: null,
        state: "uploading",
        extractionId: null,
        errorMsg: "",
      });
    }

    if (newItems.length === 0) return;

    setDocs((prev) => [...newItems, ...prev]);
    setExpandedId(newItems[0].id);

    for (const item of newItems) {
      processFile(item.file, item.id);
    }
  }, [processFile]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const removeDoc = (itemId: string) => {
    setDocs((prev) => prev.filter((d) => d.id !== itemId));
    if (expandedId === itemId) setExpandedId(null);
  };

  const allDone = docs.length > 0 && docs.every((d) => d.state === "done");

  // In compact (order-dialog) mode, fire onComplete automatically once all uploads finish
  // so the parent can invalidate its queries and reflect the attachment immediately.
  useEffect(() => {
    if (compact && allDone) {
      onComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compact, allDone]);

  return (
    <div className={cn("space-y-6", !compact && "max-w-6xl mx-auto")}>
      {/* Upload zone */}
      <Card
        className={cn(
          "border-2 border-dashed transition-colors cursor-pointer",
          dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
        )}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <CardContent className="flex flex-col items-center justify-center py-10 gap-3">
          <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
            <Upload className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-foreground">
              Drop files here or click to browse
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Supports JPG, PNG, WEBP, PDF — max 10 MB per file — multiple files allowed
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
        </CardContent>
      </Card>

      {/* Actions bar */}
      {docs.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {docs.length} document{docs.length !== 1 ? "s" : ""} · {docs.filter((d) => d.state === "done").length} complete
          </p>
          <div className="flex gap-2">
            {allDone && !compact && (
              <Button size="sm" onClick={onComplete}>
                View History
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Document items */}
      {docs.map((doc) => {
        const isExpanded = expandedId === doc.id;

        return (
          <Card key={doc.id} className="overflow-hidden">
            {/* Header row */}
            <button
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors"
              onClick={() => setExpandedId(isExpanded ? null : doc.id)}
            >
              {doc.state === "uploading" && (
                <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
              )}
              {doc.state === "done" && (
                <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
              )}
              {doc.state === "error" && (
                <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
              )}

              <span className="text-sm font-medium truncate flex-1">{doc.file.name}</span>

              {doc.state === "uploading" && (
                <span className="text-xs text-muted-foreground">Uploading…</span>
              )}
              {doc.state === "error" && (
                <span className="text-xs text-destructive truncate max-w-[200px]">{doc.errorMsg}</span>
              )}

              {(doc.state === "done" || doc.state === "error") && (
                <button
                  className="p-1 rounded hover:bg-muted shrink-0"
                  onClick={(e) => { e.stopPropagation(); removeDoc(doc.id); }}
                >
                  <X className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              )}
            </button>

            {/* Expanded — done state: full-width preview */}
            {isExpanded && doc.state === "done" && (
              <div className="border-t border-border">
                <div className="p-0 relative">
                  {doc.isPdf && doc.signedUrl ? (
                    <>
                      <object
                        data={`${doc.signedUrl}#toolbar=1&view=FitH`}
                        type="application/pdf"
                        className="w-full h-[60vh]"
                      >
                        <div className="flex flex-col items-center justify-center h-[60vh] bg-muted/30 gap-3 p-4 text-center">
                          <FileText className="h-8 w-8 text-muted-foreground" />
                          <p className="text-xs text-muted-foreground">
                            Inline PDF preview not supported in this browser.
                          </p>
                          <a
                            href={doc.signedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary underline"
                          >
                            Open PDF in new tab
                          </a>
                        </div>
                      </object>
                      <a
                        href={doc.signedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute top-2 right-2 text-xs px-2 py-1 rounded bg-background/80 backdrop-blur border border-border text-foreground hover:bg-background"
                      >
                        Open in new tab
                      </a>
                    </>
                  ) : doc.isPdf ? (
                    <div className="flex flex-col items-center justify-center h-[60vh] bg-muted/30 gap-3">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">Loading preview…</p>
                    </div>
                  ) : doc.preview ? (
                    <img src={doc.preview} alt="Document" className="w-full h-auto max-h-[60vh] object-contain bg-muted/30" />
                  ) : (
                    <div className="flex items-center justify-center py-20 text-muted-foreground bg-muted/30">No preview</div>
                  )}
                </div>
              </div>
            )}

            {/* Expanded — uploading state */}
            {isExpanded && doc.state === "uploading" && (
              <div className="border-t border-border">
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="h-10 w-10 animate-spin text-primary" />
                  <p className="font-medium text-foreground mt-4">Uploading…</p>
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
